import { T, useLocale } from "../localization";
import React from "react";

interface Props {
  openSettings: boolean;
  selectedFont: "lexend" | "opendyslexia";
  selectedSize: 14 | 16 | 18 | 20;
  onFontChange: (font: "lexend" | "opendyslexia") => void;
  onSizeChange: (size: 14 | 16 | 18 | 20) => void;
  onClose: () => void;
}

type FontSize = 14 | 16 | 18 | 20;

const SettingsModal: React.FC<Props> = (props) => {
  const { t } = useLocale();
  if (!props.openSettings) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">
            <T text="Reader Settings" />
          </h2>
          <button
            onClick={props.onClose}
            aria-label={t("Close settings")}
            title={t("Close settings")}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2"><T text="Font Style" /></h3>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => props.onFontChange("lexend")}
              className={`p-2 rounded border ${
                props.selectedFont === "lexend"
                  ? "bg-[var(--primary-color)] border-transparent text-white"
                  : "border-gray-200 hover:border-gray-300 text-gray-700"
              } font-lexend transition-colors`}
            >
              <T text="Sans-serif (Lexend)" />
            </button>
            <button
              onClick={() => props.onFontChange("opendyslexia")}
              className={`p-2 rounded border ${
                props.selectedFont === "opendyslexia"
                  ? "bg-[var(--primary-color)] border-transparent text-white"
                  : "border-gray-200 hover:border-gray-300 text-gray-700"
              } font-opendyslexia transition-colors`}
            >
              <T text="Dyslexic (OpenDyslexia)" />
            </button>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2"><T text="Font Size" /></h3>
          <div className="grid grid-cols-3 gap-2">
            {([14, 16, 18, 20] as FontSize[]).map((size) => (
              <button
                key={size}
                onClick={() => props.onSizeChange(size)}
                className={`p-2 rounded border transition-colors ${
                  props.selectedSize === size
                    ? "bg-[var(--primary-color)] border-transparent text-white"
                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                }`}
              >
                {size}px
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={props.onClose}
          className="w-full px-4 py-2 bg-[var(--primary-color)] text-white rounded hover:opacity-75 transition-colors"
        >
          <T text="Apply Settings" />
        </button>
      </div>
    </div>
  );
};

export default SettingsModal;
